import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { _createGammaTeamInvitation } from '@/api/gamma-team/mutations';
import { GAMMA_TEAM_QUERY_KEYS, GammaTeamRole } from '@/api/gamma-team/types';
import { EMAIL_REGEX } from '@/constants/static';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { Drawer } from '@/shared/design-system';

import { TeamMemberAddInvitationInfo } from '../team-member-add/team-member-add-invitation-info';
import { GammaTeamMemberAddForm } from './gamma-team-member-add-form';

export interface GammaTeamMemberAddData {
  email: string;
  role: GammaTeamRole | '';
  jobTitle: string;
}

const initialState: GammaTeamMemberAddData = { email: '', role: '', jobTitle: '' };

export const GammaTeamMemberAdd = () => {
  const { open, handleClose } = useGlobalModal('GAMMA_TEAM_MEMBER_ADD_MODAL');
  const queryClient = useQueryClient();

  const [newMember, setNewMember] = useState(initialState);
  const [step, setStep] = useState<'form' | 'invitation'>('form');
  const [formError, setFormError] = useState('');

  const { mutate } = useMutation({ mutationFn: _createGammaTeamInvitation });

  useEffect(() => {
    setFormError('');
  }, [newMember.email]);

  const handleSendInvitation = () => {
    setFormError('');
    mutate(
      {
        email: newMember.email,
        role: newMember.role as GammaTeamRole,
        jobTitle: newMember.jobTitle,
      },
      {
        onSuccess: () => {
          setStep('invitation');
          queryClient.invalidateQueries({
            queryKey: [GAMMA_TEAM_QUERY_KEYS.dashboard],
          });
        },
        onError: (err: unknown) => {
          const errMessage = (err as { response?: { _data?: { data?: { details?: string } } } })
            ?.response?._data?.data?.details;
          if (errMessage) {
            setFormError(errMessage);
            setStep('form');
          }
        },
      },
    );
  };

  const handleCloseClick = () => {
    setNewMember(initialState);
    setFormError('');
    setStep('form');
    handleClose();
  };

  const isFormStep = step === 'form';
  const isFormValid =
    EMAIL_REGEX.test(newMember.email) &&
    newMember.email.length > 1 &&
    newMember.role !== '' &&
    newMember.jobTitle.trim() !== '';

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => !next && handleCloseClick()}
      title="Add New Team Member"
      showClose
      secondaryAction={isFormStep ? { children: 'Cancel', onClick: handleCloseClick } : undefined}
      primaryAction={
        isFormStep
          ? {
              children: 'Send Invitation',
              onClick: handleSendInvitation,
              disabled: !isFormValid,
            }
          : { children: 'Ok', onClick: handleCloseClick }
      }
    >
      {isFormStep ? (
        <GammaTeamMemberAddForm data={newMember} setData={setNewMember} resError={formError} />
      ) : (
        <TeamMemberAddInvitationInfo email={newMember.email} />
      )}
    </Drawer>
  );
};
